import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Container, 
  Box, 
  Heading, 
  Tabs, 
  TabList, 
  Tab, 
  TabPanels, 
  TabPanel, 
  Spinner, 
  Alert, 
  AlertIcon,
  useColorModeValue
} from '@chakra-ui/react';
import { useAuth } from '../context/AuthContext';
import UserManagement from '../components/admin/UserManagement';
import SearchHistory from '../components/admin/SearchHistory';
import Dashboard from '../components/admin/Dashboard';
import AdminService from '../services/AdminService';

// Admin panel component
const Admin = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const { currentUser: user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const bgColor = useColorModeValue('white', 'gray.800');
  const isAdmin = user && !!user.admin;

  // Parse query parameters to set the active tab and filters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    
    // Set the active tab based on the query parameter
    if (tab === 'user-management') {
      setTabIndex(1);
    } else if (tab === 'search-history') {
      setTabIndex(2);
    } else {
      setTabIndex(0);
    }
  }, [location.search]);

  // Check if user is authenticated and has admin access
  useEffect(() => {
    const checkAdminAccess = async () => {
      setLoading(true);
      
      try {
        if (!user) {
          navigate('/login');
          return;
        }
        
        // Only users with subscriptionType 'admin' can access the admin area
        console.log('User subscription type:', user?.subscriptionType);
        if (!isAdmin) {
          setError('You do not have permission to access the admin area');
          navigate('/');
          return;
        }
        
        // Verify admin access by making a request to the admin API
        await AdminService.getDashboardStats();
        setLoading(false);
      } catch (err) {
        console.error('Admin access check failed:', err);
        setError('You do not have permission to access the admin area');
        navigate('/');
      }
    };
    
    checkAdminAccess();
  }, [user, navigate]);

  if (loading) {
    return (
      <Container centerContent maxW="container.xl" h="80vh" display="flex" alignItems="center" justifyContent="center">
        <Spinner size="xl" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container centerContent maxW="container.xl" py={5}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={4}>
      <Heading as="h1" size="lg" mb={4}>
        Admin Dashboard
      </Heading>
      
      <Box bg={bgColor} shadow="md" mb={4} borderRadius="md">
        <Tabs variant="enclosed" colorScheme="blue" index={tabIndex} onChange={(index) => setTabIndex(index)}>
          <TabList>
            <Tab>Dashboard</Tab>
            <Tab>User Management</Tab>
            <Tab>Search History</Tab>
          </TabList>
          
          <TabPanels>
            <TabPanel>
              <Dashboard />
            </TabPanel>
            <TabPanel>
              <UserManagement />
            </TabPanel>
            <TabPanel>
              <SearchHistory userId={new URLSearchParams(location.search).get('user_id')} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </Container>
  );
};

export default Admin;
